import { useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import Box from "@mui/material/Box";
import YouTubeIcon from "@mui/icons-material/YouTube";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import LinkIcon from "@mui/icons-material/Link";
import ModalUploadYoutubeVideo from "../../../Modal/ModalUploadYoutubeVideo";
import ModalGalleryYoutube from "../../../Modal/ModalGalleryYoutube";
import SegmentedControl from "../../../SegmentedControl/SegmentedControl";
import Button from "../../../Buttons/Button";
import YoutubeUrlInputField from "./YoutubeUrlInputField";
import { useTranslationApp } from "../../../../lib/i18next";
import {
  BUTTON_PUBLISH_VIDEO_ON_YOUTUBE,
  BUTTON_SELECT_FROM_YOUTUBE,
  YOUTUBE_VIDEO_SOURCE_GALLERY,
  YOUTUBE_VIDEO_SOURCE_URL,
  YOUTUBE_VIDEO_SOURCE_YOUTUBE,
} from "../../../../i18n/keysTranslations";
import { YOUTUBE_VIDEO_SOURCE } from "../../../../utils/constants";
import { appendYoutubeVideoUrl } from "../../../../utils/googlePmaxYoutubeUpload";

const UploadYoutubeVideoLink = ({
  name,
  disabled = false,
  language = "es",
  onAfterAppend = () => {},
  maxVideos = 5,
  sx = {},
}) => {
  const { t } = useTranslationApp();
  const { watch, getValues, setValue } = useFormContext();
  const [sourceMode, setSourceMode] = useState(YOUTUBE_VIDEO_SOURCE.youtube);
  const [isYoutubeGalleryOpen, setIsYoutubeGalleryOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const currentUrls = watch(name) || [];

  const sourceModeOptions = useMemo(
    () => [
      {
        value: YOUTUBE_VIDEO_SOURCE.youtube,
        label: t(YOUTUBE_VIDEO_SOURCE_YOUTUBE),
        icon: YouTubeIcon,
      },
      {
        value: YOUTUBE_VIDEO_SOURCE.gallery,
        label: t(YOUTUBE_VIDEO_SOURCE_GALLERY),
        icon: PhotoLibraryIcon,
      },
      {
        value: YOUTUBE_VIDEO_SOURCE.url,
        label: t(YOUTUBE_VIDEO_SOURCE_URL),
        icon: LinkIcon,
      },
    ],
    [t]
  );

  const activeVideoCount = (currentUrls || []).filter(Boolean).length;
  const isAtMaxVideos = activeVideoCount >= maxVideos;
  const isInteractionDisabled = disabled || isAtMaxVideos;

  const appendUrls = (youtubeUrls = []) => {
    let nextUrls = (getValues(name) || []).filter(Boolean);
    const previousLength = nextUrls.length;

    youtubeUrls.forEach((youtubeUrl) => {
      nextUrls = appendYoutubeVideoUrl({
        currentUrls: nextUrls,
        youtubeUrl,
        maxVideos,
      });
    });

    if (nextUrls.length === previousLength) {
      return;
    }

    setValue(name, nextUrls, {
      shouldDirty: true,
      shouldValidate: true,
    });
    onAfterAppend();
  };

  const onSourceModeChange = (nextMode) => {
    setSourceMode(nextMode);
  };

  const onYoutubeGallerySelected = (items) => {
    appendUrls(items.map((item) => item.youtubeUrl));
    setIsYoutubeGalleryOpen(false);
  };

  const onUploadModalSave = ({ youtubeUrl }) => {
    appendUrls([youtubeUrl]);
    setIsUploadModalOpen(false);
  };

  const onUrlAdded = ({ youtubeUrl }) => {
    appendUrls([youtubeUrl]);
  };

  const onOpenActiveModal = () => {
    if (disabled || isAtMaxVideos) {
      return;
    }

    if (sourceMode === YOUTUBE_VIDEO_SOURCE.youtube) {
      setIsYoutubeGalleryOpen(true);
      return;
    }

    if (sourceMode === YOUTUBE_VIDEO_SOURCE.gallery) {
      setIsUploadModalOpen(true);
    }
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          mb: activeVideoCount > 0 ? 3 : 0,
          ...sx,
        }}
      >
        <SegmentedControl
          name="youtubeVideoSource"
          options={sourceModeOptions}
          selectedValue={sourceMode}
          onChange={onSourceModeChange}
          readOnly={disabled}
        />

        {sourceMode === YOUTUBE_VIDEO_SOURCE.url ? (
          <YoutubeUrlInputField
            currentUrls={currentUrls}
            maxVideos={maxVideos}
            disabled={isInteractionDisabled}
            onCallbackAddUrl={onUrlAdded}
          />
        ) : null}

        {sourceMode === YOUTUBE_VIDEO_SOURCE.youtube ||
        sourceMode === YOUTUBE_VIDEO_SOURCE.gallery ? (
          <Button
            color="secondary"
            fullWidth
            disabled={isInteractionDisabled}
            onClick={onOpenActiveModal}
          >
            {sourceMode === YOUTUBE_VIDEO_SOURCE.youtube
              ? t(BUTTON_SELECT_FROM_YOUTUBE)
              : t(BUTTON_PUBLISH_VIDEO_ON_YOUTUBE)}
          </Button>
        ) : null}
      </Box>

      {isYoutubeGalleryOpen ? (
        <ModalGalleryYoutube
          modalOpen={isYoutubeGalleryOpen}
          onCloseModal={() => setIsYoutubeGalleryOpen(false)}
          onCallbackSelectedItems={onYoutubeGallerySelected}
          maxSelectable={maxVideos}
          currentUrls={currentUrls}
        />
      ) : null}

      {isUploadModalOpen ? (
        <ModalUploadYoutubeVideo
          modalOpen={isUploadModalOpen}
          onCloseModal={() => setIsUploadModalOpen(false)}
          language={language}
          onCallbackSave={onUploadModalSave}
        />
      ) : null}
    </>
  );
};

export default UploadYoutubeVideoLink;
